import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { BRANCH_LABELS } from '@lorsamthai/ops-shared';
import { listPurchaseOrders, type PurchaseOrderDoc } from '../lib/queries.js';
import { Badge } from '../ui/Badge.js';
import { fmtInt, fmtDate, daysAgo } from '../lib/format.js';
import { useAuth } from '../auth/context.js';

type Filter = 'active' | 'stuck' | 'rollover' | 'all';

const FILTER_LABEL: Record<Filter, string> = {
  active: 'กำลังดำเนินการ',
  stuck: 'ค้าง',
  rollover: '✂️ เดือนข้าม',
  all: 'ทั้งหมด',
};

export default function POList() {
  const { me } = useAuth();
  const [sp, setSp] = useSearchParams();
  const filter = (sp.get('status') as Filter) || 'active';
  const [items, setItems] = useState<PurchaseOrderDoc[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setLoading(true);
    listPurchaseOrders(filter)
      .then((r) => setItems(r.items))
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, [filter]);

  const setFilter = (f: Filter) => setSp(f === 'active' ? {} : { status: f });

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-bold">ใบสั่งซื้อ (PO)</h1>
        {me?.role === 'super_admin' && (
          <Link
            to="/pos/new"
            className="rounded-md bg-slate-900 px-3 py-1.5 text-sm font-semibold text-white"
          >
            + สั่งซื้อใหม่
          </Link>
        )}
      </div>

      <div className="flex flex-wrap gap-2">
        {(['active', 'stuck', 'rollover', 'all'] as Filter[]).map((f) => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            className={`rounded-md px-3 py-1.5 text-sm ${
              filter === f
                ? 'bg-slate-900 text-white'
                : 'border border-slate-200 bg-white text-slate-600 hover:bg-slate-50'
            }`}
          >
            {FILTER_LABEL[f]}
          </button>
        ))}
      </div>

      {error && <div className="rounded-md bg-red-50 p-3 text-sm text-red-700">{error}</div>}

      <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-left text-xs uppercase text-slate-500">
            <tr>
              <th className="px-3 py-2">PO</th>
              <th className="px-3 py-2">สาขา</th>
              <th className="px-3 py-2">สถานะ</th>
              <th className="px-3 py-2 text-right">เส้นที่สั่ง</th>
              <th className="px-3 py-2 text-right">รับแล้ว</th>
              <th className="px-3 py-2">สั่งเมื่อ</th>
              <th className="px-3 py-2">ETA / Stuck</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {items.length === 0 && !loading && (
              <tr>
                <td colSpan={7} className="px-3 py-8 text-center text-slate-500">
                  ไม่มี PO ในหมวดนี้
                </td>
              </tr>
            )}
            {items.map((po) => {
              const age = daysAgo(po.ordered_at);
              const today = new Date().toISOString().slice(0, 10);
              const isStuck =
                po.stuck_after && po.stuck_after < today &&
                ['approved', 'ordered'].includes(po.status);
              const totalOrdered = po.purchase_order_items.reduce((s, i) => s + i.qty_ordered, 0);
              const totalReceived = po.purchase_order_items.reduce((s, i) => s + i.qty_received, 0);
              return (
                <tr key={po.id} className="hover:bg-slate-50">
                  <td className="px-3 py-2 font-mono text-xs">
                    <Link to={`/pos/${po.id}`} className="hover:underline">
                      {po.po_ref ?? `#${po.id}`}
                    </Link>
                  </td>
                  <td className="px-3 py-2 text-xs">{BRANCH_LABELS[po.dest_branch]}</td>
                  <td className="px-3 py-2">
                    {isStuck ? (
                      <Badge tone="danger">ค้าง</Badge>
                    ) : po.status === 'received' ? (
                      <Badge tone="ok">รับครบ</Badge>
                    ) : po.status === 'cancelled' ? (
                      po.cancel_reason?.includes('เดือนข้าม') ? (
                        <Badge tone="neutral">✂️ เดือนข้าม</Badge>
                      ) : (
                        <Badge tone="neutral">ยกเลิก</Badge>
                      )
                    ) : (
                      <Badge tone="info">{po.status}</Badge>
                    )}
                  </td>
                  <td className="px-3 py-2 text-right font-mono">{fmtInt(totalOrdered)}</td>
                  <td className="px-3 py-2 text-right font-mono">
                    {totalReceived}
                    {totalReceived > 0 && totalReceived < totalOrdered && (
                      <span className="ml-1 text-xs text-amber-600">(บางส่วน)</span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-xs text-slate-500">
                    {fmtDate(po.ordered_at)}
                    {age !== null && <span className="ml-1">({age}d)</span>}
                  </td>
                  <td className="px-3 py-2 text-xs">
                    {isStuck ? (
                      <span className="text-red-600">stuck since {fmtDate(po.stuck_after)}</span>
                    ) : po.eta_date_revised ? (
                      <span>ETA {fmtDate(po.eta_date_revised)} (แก้ไข)</span>
                    ) : po.eta_date_promised ? (
                      <span>ETA {fmtDate(po.eta_date_promised)}</span>
                    ) : (
                      <span className="text-slate-400">—</span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
